import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { toast } from 'material-react-toastify';

import Button from '@/components/shared/Button/Button';
import { hubApi, sessionApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { CanceledBy } from '@/lib/types';
import { SessionStatus } from '@/lib/types/session-status';
import { isSuccess } from '@/lib/utils/response';
import { SessionHub } from '@addmile/backend-client';

import CoachNote from '../CoachNote/CoachNote';
import ConfirmCancelModal from '../ConfirmCancelModal/ConfirmCancelModal';

interface SessionActionsProps {
  session: SessionHub;
  fetchSession: (sessionId: string) => void;
}

const SessionActions = ({ session, fetchSession }: SessionActionsProps) => {
  const [searchParams] = useSearchParams();
  const coachId = searchParams.get('coachId');

  const [coachNote, setCoachNote] = useState<string>(session.coach?.note ?? '');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const closePopup = () => {
    setCancelModalOpen(false);
  };

  const saveCoachNote = async () => {
    const token = await getAuthToken();
    const data = await hubApi.updateSessionCoachNoteHub(
      session?.id,
      coachNote,
      withAuthHeader(token),
    );
    if (!isSuccess(data.status)) {
      toast.error(data.statusText);
    }

    toast.success('Note saved successfully');
  };

  const onCancel = () => {
    setCancelModalOpen(true);
  };

  const onCancelSession = async (cancelingBy: CanceledBy = 'coach') => {
    if (cancelingBy === 'coach') {
      const token = await getAuthToken();
      const data = await sessionApi.cancelSessionByCoachHub(
        session?.id,
        { coachId: session?.coach?.id as string, cancelReason: 'Canceled by Hub' },
        withAuthHeader(token),
      );
      if (!isSuccess(data.status)) {
        toast.error(data.statusText);
        return;
      }
    } else {
      const token = await getAuthToken();
      const data = await hubApi.cancelSessionByUserHub(
        session?.id,
        { userId: session?.user?.id, cancelReason: 'Canceled by Hub' },
        withAuthHeader(token),
      );

      if (!isSuccess(data.status)) {
        toast.error(data.statusText);
        return;
      }
    }

    closePopup();

    toast.success('Session canceled successfully');
    fetchSession(session?.id);
  };

  return (
    <>
      <ConfirmCancelModal
        closePopup={closePopup}
        isPopupOpen={cancelModalOpen}
        handleConfirm={onCancelSession}
      />
      <CoachNote coachNote={coachNote} setCoachNote={setCoachNote} />

      <div className="flex gap-4">
        <Button
          label="Save Changes"
          size="s"
          className="w-[124px] mt-8"
          variant="primary"
          onClick={saveCoachNote}
        />
        {session.status !== SessionStatus.Canceled && !coachId && (
          <Button
            label="Cancel Session"
            size="s"
            className="w-[160px] mt-8"
            prefixIcon={<img src={'/icons/close_outline.svg'} />}
            variant="transparent"
            onClick={onCancel}
          />
        )}
      </div>
    </>
  );
};

export default SessionActions;
